import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
    Button,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Spinner,
    Table,
} from "reactstrap";
import dayjs from "dayjs";
import { submitUserActivitySessionsFilter } from "model/UserActivityModel";

const DEFAULT_SESSION_PAGE_SIZE = 200;

const formatActivityDateTime = (value) => {
    if (!value) return "—";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("h:mm A MM-DD-YYYY") : String(value);
};

const normalizeSessionsResponse = (response, pageNumber = 1, pageSize = DEFAULT_SESSION_PAGE_SIZE) => {
    const payload = response?.data ?? response ?? {};
    const records = Array.isArray(payload?.records)
        ? payload.records
        : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
                ? payload
                : [];

    const resolvedPageNumber = Number(payload?.pageNumber) || pageNumber;
    const resolvedPageSize = Number(payload?.pageSize) || pageSize;
    const snOffset = (resolvedPageNumber - 1) * resolvedPageSize;

    return {
        rows: records.map((row, index) => ({
            sn: snOffset + index + 1,
            loginTime: formatActivityDateTime(
                row?.loginTime ?? row?.loginOn ?? row?.loggedOn ?? row?.createdOn
            ),
            device:
                String(row?.device ?? row?.devices ?? "")
                    .trim()
                    .toUpperCase() || "—",
            ipAddress: row?.ipAddress ?? row?.ip ?? row?.ip_address ?? "—",
        })),
        paginationMeta: {
            pageNumber: resolvedPageNumber,
            pageSize: resolvedPageSize,
            totalPages: Number(payload?.totalPages) || 0,
            totalRecords: Number(payload?.totalRecords) || 0,
        },
    };
};

const SessionPaginationBar = ({
    paginationMeta = {},
    dropdownOpen = false,
    onToggleDropdown,
    onPageSizeChange,
    onPrevPage,
    onNextPage,
}) => {
    const pageNumber = Number(paginationMeta?.pageNumber) || 1;
    const pageSize = Number(paginationMeta?.pageSize) || DEFAULT_SESSION_PAGE_SIZE;
    const totalPages = Number(paginationMeta?.totalPages) || 0;
    const totalRecords = Number(paginationMeta?.totalRecords) || 0;
    const displayFrom = totalRecords ? (pageNumber - 1) * pageSize + 1 : 0;
    const displayTo = Math.min(pageNumber * pageSize, totalRecords);

    return (
        <div className="d-flex justify-content-end align-items-center px-3 py-2 border-top bg-white user-activity-session-pagination">
            <div className="d-flex align-items-center gap-2 me-3">
                <span className="text-muted small">Rows per page:</span>
                <Dropdown isOpen={dropdownOpen} toggle={onToggleDropdown} direction="up">
                    <DropdownToggle caret color="light" size="sm" className="border bg-white">
                        {pageSize}
                    </DropdownToggle>
                    <DropdownMenu
                        container="body"
                        strategy="fixed"
                        className="user-activity-page-size-menu"
                        style={{ zIndex: 2000 }}
                    >
                        {[10, 20, 50, 100].map((size) => (
                            <DropdownItem
                                key={`user-activity-session-page-size-${size}`}
                                onClick={() => onPageSizeChange?.(size)}
                            >
                                {size}
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </Dropdown>
            </div>
            <span className="text-muted small me-3">
                {displayFrom}–{displayTo} of {totalRecords}
            </span>
            <div className="d-flex align-items-center gap-1">
                <Button
                    color="link"
                    className="text-muted p-0"
                    disabled={pageNumber <= 1}
                    onClick={onPrevPage}
                >
                    <svg width="18" height="18" fill="currentColor" aria-hidden="true">
                        <path d="M15.41 16.58L10.83 12l4.58-4.59L14 6l-6 6 6 6z" />
                    </svg>
                </Button>
                <Button
                    color="link"
                    className="text-muted p-0"
                    disabled={!totalPages || pageNumber >= totalPages}
                    onClick={onNextPage}
                >
                    <svg width="18" height="18" fill="currentColor" aria-hidden="true">
                        <path d="M8.59 16.58L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
                    </svg>
                </Button>
            </div>
        </div>
    );
};

SessionPaginationBar.propTypes = {
    paginationMeta: PropTypes.object,
    dropdownOpen: PropTypes.bool,
    onToggleDropdown: PropTypes.func,
    onPageSizeChange: PropTypes.func,
    onPrevPage: PropTypes.func,
    onNextPage: PropTypes.func,
};

const UserActivitySummaryTable = ({
    rows = [],
    loading = false,
    paginationMeta = {},
    onPageChange,
    onPageSizeChange,
}) => {
    const [expandedUserId, setExpandedUserId] = useState("");
    const [detailLoadingUserId, setDetailLoadingUserId] = useState("");
    const [detailStateByUserId, setDetailStateByUserId] = useState({});
    const [summaryDropdownOpen, setSummaryDropdownOpen] = useState(false);
    const [sessionDropdownOpen, setSessionDropdownOpen] = useState(false);

    const pageNumber = Number(paginationMeta?.pageNumber) || 1;
    const totalPages = Number(paginationMeta?.totalPages) || 0;

    const loadSessionRows = useCallback(async (row, overrides = {}) => {
        const userId = String(row?.userId ?? "").trim();
        if (!userId) return;

        const nextPageNumber = Number(overrides.pageNumber) || 1;
        const nextPageSize = Number(overrides.pageSize) || DEFAULT_SESSION_PAGE_SIZE;

        setDetailLoadingUserId(userId);
        try {
            const response = await submitUserActivitySessionsFilter({
                userId,
                pageNumber: nextPageNumber,
                pageSize: nextPageSize,
            });
            const normalized = normalizeSessionsResponse(
                response,
                nextPageNumber,
                nextPageSize
            );
            setDetailStateByUserId((prev) => ({
                ...prev,
                [userId]: normalized,
            }));
        } finally {
            setDetailLoadingUserId("");
        }
    }, []);

    const handleToggleExpand = async (row) => {
        const userId = String(row?.userId ?? "").trim();
        if (!userId) return;

        if (expandedUserId === userId) {
            setExpandedUserId("");
            setSessionDropdownOpen(false);
            return;
        }

        setExpandedUserId(userId);
        setSessionDropdownOpen(false);
        if (!detailStateByUserId[userId]) {
            await loadSessionRows(row, { pageNumber: 1, pageSize: DEFAULT_SESSION_PAGE_SIZE });
        }
    };

    const tableRows = useMemo(
        () =>
            (Array.isArray(rows) ? rows : []).map((row) => ({
                ...row,
                userId: String(row?.userId ?? "").trim(),
                name: String(row?.name ?? "").trim(),
                email: String(row?.email ?? "").trim(),
                devices: String(row?.devices ?? "").trim() || "—",
                lastLogin: formatActivityDateTime(row?.lastLogin),
                loginCount: Number(row?.loginCount) || 0,
            })),
        [rows]
    );

    const handleSummaryRowsPerPageChange = (nextSize) => {
        setExpandedUserId("");
        setSessionDropdownOpen(false);
        onPageSizeChange?.(nextSize);
    };

    const handleSummaryPrevPage = () => {
        if (pageNumber <= 1) return;
        setExpandedUserId("");
        setSessionDropdownOpen(false);
        onPageChange?.(pageNumber - 1);
    };

    const handleSummaryNextPage = () => {
        if (!totalPages || pageNumber >= totalPages) return;
        setExpandedUserId("");
        setSessionDropdownOpen(false);
        onPageChange?.(pageNumber + 1);
    };

    return (
        <div className="user-activity-summary-table-wrap border rounded">
            <div className="table-responsive mb-0">
                <Table className="user-activity-summary-table align-middle mb-0" hover>
                    <thead>
                        <tr>
                            <th className="user-activity-summary-table__expand-col" />
                            <th>Name</th>
                            <th>Email</th>
                            <th>Last Login</th>
                            <th>Device</th>
                            <th className="text-end">No Of Login</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={6} className="text-center py-5">
                                    <Spinner color="primary" />
                                </td>
                            </tr>
                        ) : tableRows.length ? (
                            tableRows.map((row, index) => {
                                const isExpanded = expandedUserId === row.userId;
                                const detailState = detailStateByUserId[row.userId] || {
                                    rows: [],
                                    paginationMeta: {},
                                };
                                const detailRows = detailState.rows || [];
                                const sessionPaginationMeta = detailState.paginationMeta || {};
                                const isDetailLoading =
                                    detailLoadingUserId === row.userId && isExpanded;

                                return (
                                    <React.Fragment key={`user-activity-summary-${row.userId || index}`}>
                                        <tr
                                            className={
                                                isExpanded
                                                    ? "user-activity-summary-table__row-expanded"
                                                    : ""
                                            }
                                        >
                                            <td className="user-activity-summary-table__expand-col">
                                                <button
                                                    type="button"
                                                    className={`user-activity-expand-btn ${
                                                        isExpanded
                                                            ? "user-activity-expand-btn--expanded"
                                                            : ""
                                                    }`}
                                                    onClick={() => handleToggleExpand(row)}
                                                    aria-label={
                                                        isExpanded
                                                            ? "Collapse user activity details"
                                                            : "Expand user activity details"
                                                    }
                                                >
                                                    <i
                                                        className={`mdi ${
                                                            isExpanded
                                                                ? "mdi-minus"
                                                                : "mdi-plus"
                                                        }`}
                                                        aria-hidden="true"
                                                    />
                                                </button>
                                            </td>
                                            <td className="fw-semibold">{row.name || "—"}</td>
                                            <td>
                                                {row.email ? (
                                                    <a
                                                        href={`mailto:${row.email}`}
                                                        className="user-activity-email-link"
                                                    >
                                                        {row.email}
                                                    </a>
                                                ) : (
                                                    "—"
                                                )}
                                            </td>
                                            <td>{row.lastLogin}</td>
                                            <td>{row.devices}</td>
                                            <td className="text-end fw-semibold">
                                                {row.loginCount}
                                            </td>
                                        </tr>
                                        {isExpanded ? (
                                            <tr className="user-activity-summary-table__detail-row">
                                                <td colSpan={6} className="p-0">
                                                    <div className="user-activity-detail-panel">
                                                        <div className="user-activity-detail-panel__meta">
                                                            <div>
                                                                <span className="user-activity-detail-panel__label">
                                                                    Email
                                                                </span>
                                                                <div className="user-activity-detail-panel__value">
                                                                    {row.email ? (
                                                                        <a
                                                                            href={`mailto:${row.email}`}
                                                                            className="user-activity-email-link"
                                                                        >
                                                                            {row.email}
                                                                        </a>
                                                                    ) : (
                                                                        "—"
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <span className="user-activity-detail-panel__label">
                                                                    Name
                                                                </span>
                                                                <div className="user-activity-detail-panel__value">
                                                                    {row.name || "—"}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {isDetailLoading ? (
                                                            <div className="text-center py-4">
                                                                <Spinner size="sm" color="primary" />
                                                            </div>
                                                        ) : detailRows.length ? (
                                                            <div className="user-activity-detail-table-wrap border rounded overflow-hidden">
                                                                <Table
                                                                    size="sm"
                                                                    className="user-activity-detail-table mb-0"
                                                                >
                                                                    <thead>
                                                                        <tr>
                                                                            <th>S.N.</th>
                                                                            <th>Login Time</th>
                                                                            <th>Device</th>
                                                                            <th>IP Address</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {detailRows.map(
                                                                            (detailRow, detailIndex) => (
                                                                                <tr
                                                                                    key={`user-activity-detail-${row.userId}-${detailIndex}`}
                                                                                >
                                                                                    <td>{detailRow.sn}</td>
                                                                                    <td>{detailRow.loginTime}</td>
                                                                                    <td>{detailRow.device}</td>
                                                                                    <td>{detailRow.ipAddress}</td>
                                                                                </tr>
                                                                            )
                                                                        )}
                                                                    </tbody>
                                                                </Table>
                                                                <SessionPaginationBar
                                                                    paginationMeta={sessionPaginationMeta}
                                                                    dropdownOpen={sessionDropdownOpen}
                                                                    onToggleDropdown={() =>
                                                                        setSessionDropdownOpen(
                                                                            (prev) => !prev
                                                                        )
                                                                    }
                                                                    onPageSizeChange={(nextSize) =>
                                                                        loadSessionRows(row, {
                                                                            pageNumber: 1,
                                                                            pageSize: nextSize,
                                                                        })
                                                                    }
                                                                    onPrevPage={() => {
                                                                        const currentPage =
                                                                            Number(
                                                                                sessionPaginationMeta?.pageNumber
                                                                            ) || 1;
                                                                        const currentPageSize =
                                                                            Number(
                                                                                sessionPaginationMeta?.pageSize
                                                                            ) || DEFAULT_SESSION_PAGE_SIZE;
                                                                        if (currentPage <= 1) return;
                                                                        loadSessionRows(row, {
                                                                            pageNumber:
                                                                                currentPage - 1,
                                                                            pageSize: currentPageSize,
                                                                        });
                                                                    }}
                                                                    onNextPage={() => {
                                                                        const currentPage =
                                                                            Number(
                                                                                sessionPaginationMeta?.pageNumber
                                                                            ) || 1;
                                                                        const currentPageSize =
                                                                            Number(
                                                                                sessionPaginationMeta?.pageSize
                                                                            ) || DEFAULT_SESSION_PAGE_SIZE;
                                                                        const sessionTotalPages =
                                                                            Number(
                                                                                sessionPaginationMeta?.totalPages
                                                                            ) || 0;
                                                                        if (
                                                                            !sessionTotalPages ||
                                                                            currentPage >=
                                                                                sessionTotalPages
                                                                        ) {
                                                                            return;
                                                                        }
                                                                        loadSessionRows(row, {
                                                                            pageNumber:
                                                                                currentPage + 1,
                                                                            pageSize: currentPageSize,
                                                                        });
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div className="text-muted text-center py-4">
                                                                No login history found
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : null}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={6} className="text-center text-muted py-5">
                                    No data found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <SessionPaginationBar
                paginationMeta={paginationMeta}
                dropdownOpen={summaryDropdownOpen}
                onToggleDropdown={() => setSummaryDropdownOpen((prev) => !prev)}
                onPageSizeChange={handleSummaryRowsPerPageChange}
                onPrevPage={handleSummaryPrevPage}
                onNextPage={handleSummaryNextPage}
            />
        </div>
    );
};

UserActivitySummaryTable.propTypes = {
    rows: PropTypes.array,
    loading: PropTypes.bool,
    paginationMeta: PropTypes.object,
    onPageChange: PropTypes.func,
    onPageSizeChange: PropTypes.func,
};

export default UserActivitySummaryTable;
